import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { isEmpty } from "lodash";
import useBasePath from "src/utils/hooks/useBasePath";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Title from "src/Pages/Company/Absences/Title/Title";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useListAbsenceTypes } from "src/Pages/Company/Absences/api/useAbsenceTypes";
import { useFetchAbsenceGroups } from "src/Pages/Company/Absences/api/useAbsenceGroups";
import { useFilteredAbsenceTypes } from "src/utils/hooks/absences/useFilteredAbsenceTypes";
import { useAbsenceTypesSettings } from "src/utils/hooks/absences/useAbsenceTypesSettings";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import transformAbsenceTypesToGroupedLists from "src/utils/Company/Absences/transformAbsenceTypesToGroupedLists";
import AbsenceTypesListSearch from "./AbsenceTypesList/AbsenceTypesListSearch";
import AbsenceTypesList from "./AbsenceTypesList/AbsenceTypesList";
import AbsenceTypesListItemSkeleton from "./AbsenceTypesList/AbsenceTypesListItemSkeleton";

const AbsenceTypes: FC = () => {
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();

  const { setAbsenceTypesSettings } = useAbsenceTypesSettings();

  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");
  const [searchQuery, setSearchQuery] = useState("");

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsenceTypes.tsx",
    });
  }

  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  const { data: absenceGroups } = useFetchAbsenceGroups(companyId);

  const {
    data: absenceTypesData,
    isFetching,
    isError,
    isSuccess,
  } = useListAbsenceTypes(companyId);

  const { active, inactive } = useMemo(
    () => transformAbsenceTypesToGroupedLists(absenceGroups, absenceTypesData),
    [absenceGroups, absenceTypesData]
  );

  const filteredActive = useFilteredAbsenceTypes(active, searchQuery);
  const filteredInactive = useFilteredAbsenceTypes(inactive, searchQuery);
  const currentData =
    activeTab === "active" ? filteredActive : filteredInactive;
  const hasContent = !isEmpty(currentData);

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTab: "active" | "inactive"
  ) => {
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: "Szablony wniosków",
          },
        ]}
      />
      <Title title={__("Szablony wniosków")} />
      <Stack direction="row" alignItems="center" spacing={2} mb="1rem">
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setAbsenceTypesSettings(absenceTypesData)}
          data-testid="absence-types-settings-button"
        >
          {__("Ustawienia widoku")}
        </Button>
        {canWriteAbsencesConfiguration && (
          <Button
            component={Link}
            variant="contained"
            to={`${basePath}/absences/absence-type/create`}
            sx={{ marginBottom: "auto" }}
            startIcon={<AddIcon />}
            data-testid="absence-types-create-button"
          >
            {__("Nowy szablon")}
          </Button>
        )}
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={handleTabChange}
          aria-label={__("Wybierz typ wymiaru")}
          sx={{
            marginLeft: "auto !important",
            "& .MuiToggleButton-root": {
              textTransform: "none",
            },
          }}
        >
          <ToggleButton value="active" aria-label={__("Szablony aktywne")}>
            {__("Aktywne")}
          </ToggleButton>
          <ToggleButton value="inactive" aria-label={__("Szablony nieaktywne")}>
            {__("Nieaktywne")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {!canWriteAbsencesConfiguration && <PermissionsAlert />}

      {isSuccess && absenceTypesData && absenceTypesData.length > 0 && (
        <AbsenceTypesListSearch
          isFetching={isFetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      )}

      {isError && <ErrorDataFetching />}

      {searchQuery && !hasContent && (
        <Typography variant="subtitle1" textAlign="center" sx={{ mt: 4 }}>
          {`${__("Brak wyników wyszukiwania dla")} "${searchQuery}"`}
        </Typography>
      )}

      <Box sx={{ display: activeTab === "active" ? "block" : "none" }}>
        {isFetching ? (
          <AbsenceTypesListItemSkeleton />
        ) : (
          <AbsenceTypesList
            label="Aktywne szablony"
            data={filteredActive}
            disabled={isFetching}
          />
        )}
      </Box>

      <Box sx={{ display: activeTab === "inactive" ? "block" : "none" }}>
        <AbsenceTypesList
          label="Nieaktywne szablony"
          data={filteredInactive}
          disabled={isFetching}
        />
      </Box>
    </>
  );
};

export default AbsenceTypes;
